import React, { useEffect, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Checkbox, Form, Grid, Icon, Popup } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import BrowCow from '@assets/images/brown-cow.jpg'
import InfoPopup from '~/components/InfoPopup'
import { ClimateCheckSelectType, HeiferRaisingMethod } from '~/api/services/climaCheck'

//calfes
import BlackCalf from '@assets/images/black-calf.png'
import GrayCalf from '@assets/images/gray-calf.png'
import BrownWhiteCalf from '@assets/images/brown-white-calf.png'
import JerseyCalf from '@assets/images/joursey-farse.png'

import { handleErrors } from '~/utils/helpers'
import { useParams } from 'react-router-dom'
import { BROWN_COW_ID } from '../../utils'
import { StyledButton, StyledField } from '../style'
import LinkPopup from '../LinkPopup'
import CO2Service from '~/api/services/co2'

interface Props {
  setIsLoadingResults: (value: boolean) => void
  youngCattleBreeds: ClimateCheckSelectType[]
  heiferRaisingMethods: HeiferRaisingMethod[]
}

const SecondStep: React.FC<Props> = (props) => {
  const { id, farmerId } = useParams()
  const { youngCattleBreeds, heiferRaisingMethods, setIsLoadingResults } = props
  const { t } = useTranslation()
  const {
    control,
    setValue,
    formState: { isSubmitted },
    clearErrors,
    reset,
    getValues
  } = useFormContext()

  const [isLoadingPredefinedData, setIsLoadingPredefinedData] = useState(false)

  const watchBreed = useWatch({ control, name: 'breed' })
  const watchYoungCattle = useWatch({ control, name: 'youngCattleBreedId' })
  const watchRaisesHeifers = useWatch({ control, name: 'raiseHeifersOnFarm' })
  const watchYoungCalfWeight = useWatch({ control, name: 'youngCattleWeightBeforeCalving' })
  const watchLostYoungCattle = useWatch({ control, name: 'lostYoungCattleWeightBeforeOneYear' })
  const watchLostYoungCattleAfter = useWatch({ control, name: 'lostYoungCattleWeightAfterOneYear' })
  const watchYoungCattleLossesBefOneYear = useWatch({ control, name: 'youngCattleLossesBeforeOneYear' })
  const watchYoungCattleLossesAfterOneYear = useWatch({ control, name: 'youngCattleLossesAfterOneYear' })
  const watchYoungCattleNumbers = useWatch({ control, name: 'youngCattleNumber' })
  const watchEka = useWatch({ control, name: 'firstCalvingAge' })

  useEffect(() => {
    const formValues = getValues()
    if (isSubmitted && !isNaN(watchRaisesHeifers)) {
      clearErrors('raiseHeifersOnFarm')
    }
    if (!watchRaisesHeifers) {
      reset({
        ...formValues,
        firstCalvingAge: '',
        youngCattleDieselConsumption: '',
        youngCattleLitterQuantity: '',
        youngCattleNumber: '',
        youngCattlePastureDays: '',
        youngCattleWeightBeforeCalving: '',
        rearingPhases: [],
        rearingPhasesType: '',
        calvesEnergyConsumptions: [],
        calvesHousingTypes: [],
        calvesManureStorageTypes: [],
        calvesConcentratedFeeds: [],
        calvesCoarseFeeds: []
      })
    } else if (watchRaisesHeifers && watchBreed !== BROWN_COW_ID && !formValues.firstCalvingAge) {
      fetchPredefinedData(watchBreed)
    }
  }, [watchRaisesHeifers])

  useEffect(() => {
    if (parseInt(watchYoungCattleLossesBefOneYear) === 0) {
      setValue('lostYoungCattleWeightBeforeOneYear', 0)
    }

    if (parseInt(watchYoungCattleLossesAfterOneYear) === 0) {
      setValue('lostYoungCattleWeightAfterOneYear', 0)
    }
  }, [watchYoungCattleLossesBefOneYear, watchYoungCattleLossesAfterOneYear])

  async function fetchPredefinedData(value) {
    setIsLoadingPredefinedData(true)
    setIsLoadingResults(true)
    try {
      const formValues = getValues()
      const response = await CO2Service.getHeifersPredData(
        id,
        {
          breeds: formValues.value,
          tax: formValues.tax,
          farmerId,
          youngCattleBreedId: value
        }
      )

      reset({
        ...formValues,
        ...response.data,
        youngCattleWeightBeforeCalving: '530',
        rearingPhases: [],
        firstCalvingAge: '',
        youngCattleDieselConsumption: ''
      })
      setValue('youngCattleBreedId', value)
    } catch (error) {
      handleErrors(error)
    }
    setIsLoadingPredefinedData(false)
    setIsLoadingResults(false)
  }

  function renderFields() {
    const ekaValue = watchEka ? parseFloat(watchEka.replace(',', '.')) : 0
    return <div style={{ marginTop: '2em' }}>
      <Grid columns={3}>
        <Grid.Row>
          <Grid.Column>
            <FormFields.Select
              control={control}
              name={'heiferRaisingMethodId'}
              disabled={isLoadingPredefinedData}
              withoutAsterisk
              options={heiferRaisingMethods.map(heifer => ({ value: heifer.id, text: heifer.label }))}
              label={<div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: '0.5em' }}>
                <label>{'Produktionsverfahren'}</label>
                <InfoPopup
                  content={`Wählen Sie hier das Produktionsverfahren aus, das Sie auch im Rahmen Ihrer Berichtspflicht gemäß Düngeverordnung (DüV) zur Berechnung des N-Anfalls aus der Tierhaltung angegeben haben. 
                    Nehmen Ihre Färsen über 75 % der gesamten Grobfutter Trockenmasse über Grasprodukte von Wiesen und Weiden auf, ist Ihr Betrieb ein Grünlandbetrieb. 
                    Bei einer geringeren Aufnahme ist Stallbetrieb zu wählen. Grünland extensiv bezeichnet hierbei Betriebe, deren Grobfutter von Naturschutzflächen stammt.`}
                  position={'top center'}
                  styleIcon={{ fontSize: '1.2em' }}
                />
                <LinkPopup
                  content={'DüV'}
                />
              </div>}
              decimalScale={1}
              typeInput='controled'
              noErrorMessage
              rules={{
                required: true
              }}
              placeholder={'Produktionsverfahren auswählen'}
            />
          </Grid.Column>

          <Grid.Column>
            <FormFields.Input
              disabled={isLoadingPredefinedData}
              control={control}
              name={'firstCalvingAge'}
              noErrorMessage={!watchEka}
              decimalScale={1}
              allowDecimals
              suffix={' Monate'}
              styleErrorLabel={{ position: 'absolute', zIndex: 999, left: 20, marginTop: '0.5em', maxWidth: 300 }}
              withoutAsterisk
              label={<div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: '0.5em' }}>
                <label>{'Erstkalbealter (EKA)'}</label>
                <LinkPopup
                  content={'LKV-Bericht, Herdensoftware - "EKA"'}
                />
                {(ekaValue < 23 || ekaValue > 28) && <Popup
                  content={'Das Erstkalbealter liegt außerhalb des Normalbereichs von mindestens 23,0 Monaten und maximal 28,0 Monaten.'}
                  trigger={<Icon name='warning sign' color='yellow' style={{ fontSize: '1.2em' }} />}
                />}
              </div>}
              typeInput='controled'
              rules={{
                validate: (value) => {
                  const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                  if (transformValue < 20 || transformValue > 35) {
                    return 'Das eingegebene Erstkalbealter muss mindestens 20,0 Monate und darf maximal 35,0 Monate betragen.'
                  }
                  return true
                },
                required: true
              }}
              placeholder={'Anzahl Monate'}
            />
          </Grid.Column>

          <Grid.Column>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column>
            <FormFields.Input
              control={control}
              disabled={isLoadingPredefinedData}
              name={'youngCattleNumber'}
              suffix={' Färse(n)'}
              withoutAsterisk
              label={<div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: '0.5em' }}>
                <label>{t('⌀ inventory')}</label>
                <LinkPopup
                  content={'HITier (Bestandsregister, "weiblich, ohne Abkalbung")'}
                />
                {watchYoungCattleNumbers > 2000 && <Popup
                  content={'Der ⌀-Bestand scheint sehr hoch zu sein. Sind Sie sicher, dass dieser Wert stimmt?'}
                  trigger={<Icon name='warning sign' color='yellow' style={{ fontSize: '1.2em' }} />}
                />}
              </div>}
              typeInput='controled'
              noErrorMessage
              rules={{
                required: true,
                min: 3,
                max: 6000
              }}
              placeholder={'Anzahl Färse(n)'}
            />
          </Grid.Column>

          <Grid.Column>
            <FormFields.Input
              disabled={isLoadingPredefinedData}
              control={control}
              noErrorMessage
              name={'youngCattleLossesBeforeOneYear'}
              styleErrorLabel={{ position: 'absolute', zIndex: 999, left: 20, marginTop: '0.5em', maxWidth: 300 }}
              suffix={' Färse(n)'}
              withoutAsterisk
              label={<div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: '0.5em' }}>
                <label>{'Anzahl der Verluste von 29 LT bis 1 Jahr'}</label>
                <LinkPopup
                  content={'HITier, Kennung "VE" und "TOE" oder BZA'}
                />
                {watchYoungCattleLossesBefOneYear > watchYoungCattleNumbers * 0.3 && <Popup
                  content={'Die Anzahl der Verluste liegt über dem Normalbereich von 30%  des Gesamtbestandes der Färsen.'}
                  trigger={<Icon name='warning sign' color='yellow' style={{ fontSize: '1.2em' }} />}
                />}
              </div>}
              typeInput='controled'
              rules={{
                required: true,
                min: 0,
                max: 100
              }}
              placeholder={'Anzahl Färse(n)'}
            />
          </Grid.Column>

          <Grid.Column>
            <FormFields.Input
              control={control}
              disabled={isLoadingPredefinedData}
              noErrorMessage
              name={'youngCattleLossesAfterOneYear'}
              typeInput='controled'
              withoutAsterisk
              rules={{
                required: true,
                min: 0,
                max: 100
              }}
              suffix=' Färse(n)'
              label={<div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: '0.5em' }}>
                <label>{'Anzahl der Verluste über 1 Jahr'}</label>
                <LinkPopup
                  content={'HITier, Kennung "VE" und "TOE" oder BZA'}
                />
                {watchYoungCattleLossesAfterOneYear > watchYoungCattleNumbers * 0.3 && <Popup
                  content={'Die Anzahl der Verluste liegt über dem Normalbereich von 30%  des Gesamtbestandes der Färsen.'}
                  trigger={<Icon name='warning sign' color='yellow' style={{ fontSize: '1.2em' }} />}
                />}
              </div>}
              placeholder={'Anzahl Färse(n)'}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column>
            <StyledField>
              <label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div>{'Gewicht vor Abkalbung'}</div>
                  <InfoPopup
                    content={`Vorgeschlagener Wert: 84 % des Altkuhgewichtes. Jungrinder sind noch nicht ausgewachsen und wiegen deshalb weniger als Ihre Altkühe. 
                    Inkl. Kalb und Fruchtwasser sind die Tiere allerdings ca. 70 - 90 kg schwerer als kurz nach dem Kalben.`}
                    position={'top center'}
                    styleIcon={{ fontSize: '1.2em' }}
                  />
                </div>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginTop: 8 }}>
                <StyledButton
                  type='button'
                  icon='minus'
                  disabled={watchYoungCalfWeight - 10 <= 0 || !watchYoungCalfWeight}
                  onClick={() => {
                    if (!watchYoungCalfWeight) {
                      return
                    }
                    const numberYoungCattle = parseInt(watchYoungCalfWeight) - 10
                    if (numberYoungCattle >= 350 && numberYoungCattle <= 800) {
                      clearErrors('youngCattleWeightBeforeCalving')
                    }
                    setValue('youngCattleWeightBeforeCalving', numberYoungCattle)
                  }}
                />

                <FormFields.Input
                  control={control}
                  disabled={isLoadingPredefinedData}
                  name={'youngCattleWeightBeforeCalving'}
                  noErrorMessage={!watchYoungCalfWeight}
                  typeInput='controled'
                  withoutAsterisk
                  rules={{
                    validate: (value) => {
                      const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                      if (transformValue < 350 || transformValue > 800) {
                        return 'Das Gewicht vor Abkalbung muss mindestens 350 kg und darf maximal 800 kg betragen.'
                      }
                      return true
                    },
                    required: true
                  }}
                  suffix=' kg Lebendmasse'
                  placeholder={'in kg Lebendmasse'}
                />
                <StyledButton
                  type='button'
                  onClick={() => {
                    const numberYoungCattle = parseInt(watchYoungCalfWeight || 0) + 10
                    if (numberYoungCattle >= 350 && numberYoungCattle <= 800) {
                      clearErrors('youngCattleWeightBeforeCalving')
                    }
                    setValue('youngCattleWeightBeforeCalving', numberYoungCattle)
                  }}
                  icon='add'
                />
              </div>
            </StyledField>
          </Grid.Column>

          <Grid.Column>
            <StyledField>
              <label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div>{'Gewicht der Verluste von 29 LT bis 1 Jahr'}</div>
                </div>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginTop: 8 }}>
                <StyledButton
                  type='button'
                  icon='minus'
                  disabled={(watchLostYoungCattle - 10 <= 0 || !watchLostYoungCattle) || parseInt(watchYoungCattleLossesBefOneYear) === 0}
                  onClick={() => {
                    if (!watchLostYoungCattle) {
                      return
                    }
                    const numberYoungCattle = parseInt(watchLostYoungCattle) - 10
                    if (numberYoungCattle >= 40 && numberYoungCattle <= 500) {
                      clearErrors('lostYoungCattleWeightBeforeOneYear')
                    }
                    setValue('lostYoungCattleWeightBeforeOneYear', numberYoungCattle)
                  }}
                />
                <FormFields.Input
                  control={control}
                  disabled={isLoadingPredefinedData || parseInt(watchYoungCattleLossesBefOneYear) === 0}
                  name={'lostYoungCattleWeightBeforeOneYear'}
                  suffix={' kg Lebendmasse'}
                  styleErrorLabel={{ position: 'absolute', zIndex: 999, left: 20, marginTop: '0.5em', maxWidth: 300 }}
                  withoutAsterisk
                  typeInput='controled'
                  noErrorMessage={!watchLostYoungCattle}
                  rules={{
                    validate: (value) => {
                      if (parseInt(watchYoungCattleLossesBefOneYear) === 0) {
                        return true
                      }
                      const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                      if (transformValue < 40 || transformValue > 500) {
                        return 'Gewicht der Verluste muss mindestens 40 kg und darf maximal 500 kg betragen.'
                      }
                      return true
                    },
                    required: true,
                    max: {
                      value: watchLostYoungCattleAfter && watchLostYoungCattleAfter > 0 ? watchLostYoungCattleAfter : 500,
                      message: 'Das eingegebene Gewicht für Verluste bis 1 Jahr kann nicht größer sein als für die Verluste über einem Jahr.'
                    }
                  }}
                  placeholder={'in kg Lebendmasse'}
                />
                <StyledButton
                  type='button'
                  disabled={parseInt(watchYoungCattleLossesBefOneYear) === 0}
                  onClick={() => {
                    const numberYoungCattle = parseInt(watchLostYoungCattle || 0) + 10
                    if (numberYoungCattle >= 40 && numberYoungCattle <= 500) {
                      clearErrors('lostYoungCattleWeightBeforeOneYear')
                    }
                    setValue('lostYoungCattleWeightBeforeOneYear', numberYoungCattle)
                  }}
                  icon='add'
                />
              </div>
            </StyledField>
          </Grid.Column>
          <Grid.Column>
            <StyledField>
              <label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div>{'Gewicht der Verluste über 1 Jahr'}</div>
                </div>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 15, marginTop: 8 }}>
                <StyledButton
                  type='button'
                  icon='minus'
                  disabled={(watchLostYoungCattleAfter - 10 <= 0 || !watchLostYoungCattleAfter) || parseInt(watchYoungCattleLossesAfterOneYear) === 0}
                  onClick={() => {
                    if (!watchLostYoungCattleAfter) {
                      return
                    }
                    const numberYoungCattle = parseInt(watchLostYoungCattleAfter) - 10
                    if (numberYoungCattle >= 100 && numberYoungCattle <= 1000) {
                      clearErrors('lostYoungCattleWeightAfterOneYear')
                    }
                    setValue('lostYoungCattleWeightAfterOneYear', numberYoungCattle)
                  }}
                />
                <FormFields.Input
                  control={control}
                  disabled={isLoadingPredefinedData || parseInt(watchYoungCattleLossesAfterOneYear) === 0}
                  name={'lostYoungCattleWeightAfterOneYear'}
                  noErrorMessage={!watchLostYoungCattleAfter}
                  styleErrorLabel={{ position: 'absolute', zIndex: 999, left: 20, marginTop: '0.5em', maxWidth: 300 }}
                  typeInput='controled'
                  withoutAsterisk
                  rules={{
                    validate: (value) => {
                      if (parseInt(watchYoungCattleLossesAfterOneYear) === 0) {
                        return true
                      }
                      const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
                      if (transformValue > watchYoungCalfWeight + 100) {
                        return 'Das eingegebene Gewicht kann maximal +100kg über dem Gewicht vor der Abkalbung liegen.'
                      }
                      if (transformValue < 100 || transformValue > 1000) {
                        return false
                      }
                      return true
                    },
                    required: true
                  }}
                  suffix=' kg Lebendmasse'
                  placeholder={'in kg Lebendmasse'}
                />
                <StyledButton
                  type='button'
                  disabled={parseInt(watchYoungCattleLossesAfterOneYear) === 0}
                  onClick={() => {
                    const numberYoungCattle = parseInt(watchLostYoungCattleAfter || 0) + 10
                    if (numberYoungCattle >= 200 && numberYoungCattle <= 1000) {
                      clearErrors('lostYoungCattleWeightAfterOneYear')
                    }
                    setValue('lostYoungCattleWeightAfterOneYear', parseInt(watchLostYoungCattleAfter || 0) + 10)
                  }}
                  icon='add'
                />
              </div>
            </StyledField>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  }

  function getSourceCalf(breed) {
    switch (breed.id) {
      case 1:
        return BrownWhiteCalf
      case 2:
        return GrayCalf
      case 3:
        return BrowCow
      case 4:
        return BlackCalf
      case 5:
        return JerseyCalf
    }
  }

  function renderRace() {
    return <Form.Field>
      <label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div>{t('Race')}</div>
          <InfoPopup
            content={t('First, click on the main breed you have on your farm.')}
            styleIcon={{ fontSize: '1.2em' }}
            position={'top center'}
          />
        </div>
      </label>
      <Form.Group widths={'equal'} style={{ alignItems: 'center' }}>
        {youngCattleBreeds?.map((youngCattle) => {
          return <Form.Field key={youngCattle.id} style={{ position: 'relative' }}>
            <Checkbox
              disabled={isLoadingPredefinedData}
              label={
                <label style={{ whiteSpace: 'nowrap', minWidth: 200 }}>
                  <img
                    src={getSourceCalf(youngCattle)}
                    style={{
                      width: 100,
                      marginRight: 10,
                      height: 100,
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: 70,
                      display: 'flex'
                    }}>
                    <div>{youngCattle.label}</div>
                  </div>

                </label>
              }
              checked={watchYoungCattle === youngCattle.id}
              onChange={() => {
                fetchPredefinedData(youngCattle.id)
              }
              }
            />
          </Form.Field>
        })}
      </Form.Group>
    </Form.Field>
  }

  function renderHeifersFarmSelect() {
    return <Controller
      rules={{ required: true }}
      name='raiseHeifersOnFarm'
      render={({ fieldState: { error } }) => (
        <Form.Field error={Boolean(error)}>
          <label>{'Ziehen Sie Ihre Färsen auf dem Betrieb selber auf?'}</label>
          <div style={{ display: 'flex', gap: 20 }}>
            <Checkbox
              radio
              label='Ja'
              disabled={isLoadingPredefinedData}
              checked={watchRaisesHeifers === 1}
              onClick={() => setValue('raiseHeifersOnFarm', 1)}
            />
            <Checkbox
              radio
              disabled={isLoadingPredefinedData}
              label='Nein'
              checked={watchRaisesHeifers === 0}
              onClick={() => setValue('raiseHeifersOnFarm', 0)}
            />
          </div>
        </Form.Field>
      )}
    />
  }

  const displayRaceSelection = watchBreed === BROWN_COW_ID

  return <>
    {renderHeifersFarmSelect()}
    {watchRaisesHeifers === 1 && <>
      {displayRaceSelection && renderRace()}
      {watchYoungCattle && youngCattleBreeds && renderFields()}
    </>}
  </>
}
export default SecondStep